import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteIcon from "@mui/icons-material/EditNote";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { default as React, useEffect, useState, useCallback } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import defaultImage from "../../assets/images/courseImage/images.jpg";
import ReadMore from "../../components/ReadMore.jsx";
import "../../css/Image-cropper-modal.css";
import ImageCropper from "../../components/ImageCropperModal/ImageCropper.jsx";
import { SwitchContainer } from "../../style/Switch/index.jsx";
import {
  AddNewCourse,
  CourseValidationCheck,
  fetchActiveCourseCategories,
  GetCourseData,
  ToggleCourseFeaturedAPI,
} from "../../services/courseServices.js";
import { DashBoardContainer } from "../../style/AddCourseStyle";
import DashboardHeader from "../../components/Common/DashboardHeader";
import Switch from "../../components/Switch.jsx";
import IosShareIcon from "@mui/icons-material/IosShare";

const AddCourse = () => {
  const [course, setCourse] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [courseImage, setCourseImage] = useState(null);
  const [cropImage, setCropImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [searchString, setSearchString] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    courseName: "",
    courseNumber: "",
    courseDescription: "",
    courseCategory: "",
    categoryId: "",
    status: "",
    document: "",
  });

  useEffect(() => {
    const handleScroll = () => {
      setShowFloatingButton(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getCategories = async () => {
    try {
      const response = await fetchActiveCourseCategories();
      if (response.success) {
        setCategories(response?.categories);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
      toast.error(error?.response?.data?.error ?? "Network Error");
    }
  };

  const getCourse = async (pageNum = 0, isNewSearch = false) => {
    try {
      setLoading(true);
      const response = await GetCourseData({
        page: pageNum,
        offset: 18,
        search: searchString,
      });
      if (response.success) {
        const newCourses = response?.data?.courses || [];
        setCourse((prev) =>
          isNewSearch || pageNum === 0 ? newCourses : [...prev, ...newCourses]
        );
        setHasMore(response?.data?.hasMore || false);
        setPage(response?.data?.nextPage || 0);
        setTotalCount(response?.data?.totalData || 0);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error ?? "Network Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCategories();
    getCourse();
  }, []);

  const fetchMoreData = () => {
    if (!loading && hasMore) getCourse(page, false);
  };

  const handleFeatured = async (courseId, isFeatured) => {
    try {
      const response = await ToggleCourseFeaturedAPI(courseId, {
        featured: isFeatured,
      });
      if (response.success) {
        toast.success(response.message || "Course updated successfully");
      } else {
        toast.error("Failed to update featured status");
      }
    } catch (error) {
      toast.error(error?.response?.data?.error ?? "Network Error");
      setCourse((prev) =>
        prev.map((c) =>
          c.id === courseId ? { ...c, featured: !isFeatured } : c
        )
      );
    }
  };

  const debounceSearch = useCallback(() => {
    const handler = setTimeout(() => {
      getCourse(0, true);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchString]);

  useEffect(() => {
    const cleanup = debounceSearch();
    return cleanup;
  }, [searchString, debounceSearch]);

  const handleSearch = (e) => {
    setSearchString(e.currentTarget.value);
    setPage(0);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const { courseName, courseNumber, courseDescription, courseCategory } =
        formData;

      if (
        !courseName.trim() ||
        !courseNumber.trim() ||
        !courseDescription.trim() ||
        !courseCategory.trim()
      ) {
        toast.warning("Please fill all required fields");
        return;
      }

      if (!courseImage) {
        toast.warning("Please select a thumbnail for the course");
        return;
      }

      await CourseValidationCheck({
        payload: {
          courseName: courseName.trim(),
          courseNumber: courseNumber.trim(),
        },
      });

      const data = new FormData();
      data.append("name", courseName);
      data.append("courseNumber", courseNumber);
      data.append("description", courseDescription);
      data.append("categoryId", formData.categoryId);
      data.append("categoryName", courseCategory);
      data.append("status", formData.status);
      data.append("document", courseImage);

      const response = await AddNewCourse({ payload: data });

      if (response.success) {
        toast.success("New Course Added Successfully");
        getCourse(0, true);
        setShowModal(false);
        setFormData({
          courseName: "",
          courseNumber: "",
          courseDescription: "",
          courseCategory: "",
          categoryId: "",
          status: "",
          document: "",
        });
        setCourseImage(null);
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error?.response?.data?.error ?? "Network Error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategoryChange = (e) => {
    const selectedIndex = e.target.selectedIndex;
    const selectedCategory = categories[selectedIndex - 1];

    setFormData({
      ...formData,
      courseCategory: e.target.value,
      categoryId: selectedCategory?.id || "",
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setCropImage(reader.result);
      reader.readAsDataURL(file);
      setShowCropper(true);
    }
  };

  const handleInputClose = () => {
    setShowModal(false);
    setFormData({
      courseName: "",
      courseNumber: "",
      courseDescription: "",
      courseCategory: "",
      status: "",
    });
  };

  const CourseItem = ({ item }) => {
    const [checked, setChecked] = useState(item.featured);

    const handleSwitchChange = () => {
      const newStatus = !checked;
      setChecked(newStatus);
      handleFeatured(item.id, newStatus);
    };

    return (
      <div className="lms-card" key={item.id}>
        <div className="card-image-container">
          <div className="image-icon-container">
            <Link to={`/course/${item.id}`}>
              <IosShareIcon className="img-icon" />
            </Link>
          </div>
          <img
            src={item.thumbnail || defaultImage}
            alt={`${item.name} course`}
            className="card-top-image"
            loading="lazy"
          />
        </div>

        <div className="card-body">
          <div className="paragraphs">
            <p>Course Name: <strong>{item.name}</strong></p>
            <p>Course Number: <strong>{item.courseNumber}</strong></p>
            <p>Course Description: <strong className="Description"><ReadMore>{item.description}</ReadMore></strong></p>
            <p>Course Category: <strong>{item.categoryName}</strong></p>
            <p>Created Date & Time: <strong>{item.createdAt}</strong></p>
            <p>Status: <strong>{item.status}</strong></p>
            {item.status !== "published" ? (
              <p>Actions:
                <span className="action-gap">
                 { item.status==="draft" &&  <Link to="/add-modules" state={{ data: item }}><EditNoteIcon /></Link>}
                 { item.status==="pending exam" && <Link to="/exam-creation-list" state={{ courseId: item.id, courseName: item.name }}><NoteAltOutlinedIcon /></Link>}
                </span>
              </p>
            ) : (
              <div className="course-featured">
                <p>Course Featured:</p>
                <span><SwitchContainer><Switch checked={checked} onChange={handleSwitchChange} /></SwitchContainer></span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashBoardContainer>
      <DashboardHeader title="Course Overview" />
      <div className="dashboard-container">
        <div className="header">
          <div className="header-input">
            <input
              className="search-box"
              type="text"
              placeholder="Search with Course name"
              onChange={handleSearch}
              value={searchString}
            />
            <SearchIcon className="search-icon" />
          </div>
          <button className="create-button" onClick={() => setShowModal(true)}>Create Course</button>
        </div>

        <div className="sub-header">
          <p>TOTAL COURSE ({totalCount})</p>
        </div>

        {course.length > 0 ? (
          <InfiniteScroll
            dataLength={course.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={<div className="infinite-scroll-loading">Loading more courses...</div>}
            className="lms-list"
          >
            {course.map((item) => (
              <CourseItem item={item} key={item.id} />
            ))}
          </InfiniteScroll>
        ) : (
          <div className="infinite-scroll-loading">
            {loading ? "Loading courses..." : "No courses found"}
          </div>
        )}

        {showFloatingButton && (
          <div className="floating-action-button" onClick={() => setShowModal(true)}>
            <AddIcon />
          </div>
        )}

        {showModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Create Course</h5>
                <button className="close-button" onClick={handleInputClose}><CloseIcon /></button>
              </div>
              <div className="input-wrapper">
                <div className="modal-inputs-2">
                  <div className="input-para">
                    <p>Name</p>
                    <input className="modal-input" type="text" name="courseName" value={formData.courseName} onChange={(e) => setFormData({ ...formData, courseName: e.target.value })} />
                  </div>
                  <div className="input-para">
                    <p>Course No.</p>
                    <input className="modal-input" type="text" name="courseNumber" value={formData.courseNumber} onChange={(e) => setFormData({ ...formData, courseNumber: e.target.value })} />
                  </div>
                </div>
                <div className="course-category">
                  <p>Course Category</p>
                  <select name="courseCategory" value={formData.courseCategory} onChange={handleCategoryChange}>
                    <option value="" disabled>Select a category</option>
                    {categories.map((key) => (
                      <option key={key.id} value={key.name}>{key.name}</option>
                    ))}
                  </select>
                </div>
                <div className="input-para">
                  <p>Course Description</p>
                  <textarea className="modal-input" name="courseDescription" value={formData.courseDescription} onChange={(e) => setFormData({ ...formData, courseDescription: e.target.value })} />
                </div>
                <div className="input-para">
                  <p>Course Image</p>
                  <input type="file" accept="image/*" onChange={handleFileChange} />
                  <ImageCropper
                    open={showCropper}
                    onClose={() => setShowCropper(false)}
                    imageSrc={cropImage}
                    onCropComplete={(croppedImg) => {
                      setCourseImage(croppedImg);
                      setShowCropper(false);
                    }}
                    aspectRatio={16 / 9}
                    title="Course Image Cropper"
                  />
                </div>
                <div className="btn">
                  <button className="submit-button" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? "CREATING..." : "CREATE"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <ToastContainer />
    </DashBoardContainer>
  );
};

export default AddCourse;
